import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface ToggleItem {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  separator?: boolean;
}

export interface ToggleProps {
  trigger: ReactNode;
  items: ToggleItem[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  position?: 'left' | 'right' | 'center';
  className?: string;
  menuClassName?: string;
}

export default function Toggle({
  trigger,
  items,
  isOpen,
  onToggle,
  onClose,
  position = 'center',
  className = '',
  menuClassName = '',
}: ToggleProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const toggleRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // ESC 키 핸들링
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // 토글 애니메이션 처리
  useEffect(() => {
    if (isOpen) {
      // 토글이 열릴 때
      setIsVisible(true);
      // 다음 프레임에서 애니메이션 시작
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // 토글이 닫힐 때
      setIsAnimating(false);
      // 애니메이션 완료 후 DOM에서 제거
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 150); // transition duration과 일치
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const positionClasses = {
    left: 'left-0',
    right: 'right-0',
    center: 'left-1/2 -translate-x-1/2',
  };

  const handleItemClick = (item: ToggleItem) => {
    item.onClick();
    onClose();
  };

  return (
    <div ref={toggleRef} className={`relative ${className}`}>
      {/* Trigger */}
      <div onClick={onToggle} className="cursor-pointer">
        {trigger}
      </div>

      {/* Dropdown Menu */}
      {isVisible && (
        <div
          className={`absolute ${positionClasses[position]} top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 transition-all duration-150 ease-out ${
            isAnimating
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 -translate-y-2'
          } ${menuClassName}`}
        >
          {items.map((item, index) => (
            <div key={item.id}>
              {item.separator && index > 0 && (
                <hr className="my-1 border-gray-200" />
              )}
              <button
                onClick={() => handleItemClick(item)}
                className={`w-full text-left px-4 py-2 flex items-center space-x-2 transition-colors duration-200 ${
                  item.variant === 'danger'
                    ? 'text-red-600 hover:bg-red-50'
                    : 'text-gray-700 hover:bg-gray-100'
                } cursor-pointer`}
              >
                {item.icon && (
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={item.icon}></i>
                  </div>
                )}
                <span className="whitespace-nowrap">{item.label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
